/*
 * How many overlays are currently up, counted process-wide rather than per-hook
 * so a host app's document-level key handling can tell an Escape that dismisses
 * something from an Escape that means something else (Companion's Esc-Esc
 * switch-user is the one that matters: a dismissing Escape must never count).
 *
 * Kit-owned on purpose. The kit cannot import app code, and the overlays whose
 * openness is being counted are the kit's own - so the bookkeeping lives here
 * and the app reads it, never the other way round.
 *
 * ORDERING INVARIANT, and the reason a plain count is enough: registration is
 * keyed on the React `open` that drives the overlay, and every release runs in
 * an effect cleanup. Neither the platform nor React can get there before the
 * keydown that dismissed the overlay has finished dispatching - a native
 * <dialog>'s cancel/close and the Popover API's light dismiss are default
 * actions of that keydown, which the browser runs only after every listener
 * has, and React flushes the resulting state change later still. So a
 * document-level handler reading this count during the Escape keydown always
 * still sees the overlay that Escape is about to close.
 */
let openCount = 0;

/**
 * How many kit overlays are open right now. Safe to read synchronously from a
 * key handler: see the ordering invariant in this module.
 */
export const getOpenDismissableCount = (): number => openCount;

/**
 * Count one open overlay, returning its release. Call from an effect that runs
 * while the overlay is open and release it in that effect's cleanup. Releasing
 * more than once is a no-op.
 *
 * `useDialog` and `usePopover` do this for every overlay they drive, so kit
 * overlays are counted automatically. It is exported for overlays the kit does
 * not drive (a host app's own popup with its own Escape handling), which have
 * to opt in by hand or they will not be visible to the host's Escape guard.
 */
export function registerDismissable(): () => void {
  openCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openCount -= 1;
  };
}
